import React, { useEffect, useState } from 'react';
import PageBanner from '../../components/PageBanner';
import { FaAtom, FaBookOpen, FaExternalLinkAlt, FaFlask, FaMicroscope, FaSeedling, FaUsers } from 'react-icons/fa';
import { IoArrowForwardOutline } from 'react-icons/io5';
import { getResearch } from '../../services/data.service';
import { mediaUrl } from '../../services/assetUrl';

// Icon key -> react-icon, for focus-area cards.
const FOCUS_ICONS = {
    microscope: <FaMicroscope />,
    seedling: <FaSeedling />,
    atom: <FaAtom />,
    users: <FaUsers />,
    flask: <FaFlask />,
    book: <FaBookOpen />,
};

// Icon key -> react-icon, for leadership profile links.
const LINK_ICONS = {
    book: <FaBookOpen />,
    external: <FaExternalLinkAlt />,
    flask: <FaFlask />,
};

const ResearchMain = () => {
    const [model, setModel] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getResearch();
                if (active) setModel(res?.data || null);
            } catch (e) {
                if (active) setModel(null);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    if (loading || !model) {
        return (
            <div className="research-main">
                <div className="container" style={{ padding: '6rem 0', textAlign: 'center', color: '#53545b' }}>
                    {loading ? 'Loading…' : 'Research content is currently unavailable. Please check back later.'}
                </div>
            </div>
        );
    }

    const hero = model.hero || {};
    const overview = model.overview || {};
    const leadership = model.leadership || {};
    const profile = leadership.profile || {};
    const focus = model.focus || {};
    const resources = model.resources || {};

    return (
        <div className="research-main">
            <PageBanner
                title={hero.title}
                eyebrow={hero.eyebrow}
                subtitle={hero.subtitle}
                image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
            />

            <div className="container research-main__body">
                <section className="research-overview">
                    <div className="research-overview__copy">
                        <span className="research-kicker">{overview.kicker}</span>
                        <h2>{overview.heading}</h2>
                        {(overview.paragraphs || []).map((p, idx) => (
                            <p key={idx}>{p}</p>
                        ))}
                    </div>

                    <div className="research-overview__stats">
                        {(overview.stats || []).map((item) => (
                            <article key={item.label} className="research-stat-card">
                                <span className="research-stat-card__label">{item.label}</span>
                                <strong>{item.value}</strong>
                                <p>{item.note}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="research-leadership">
                    <div className="research-leadership__profile">
                        <div className="avatar">{profile.initials}</div>
                        <div>
                            <h3>{profile.name}</h3>
                            <p>{profile.role}</p>
                            <span>{profile.location}</span>
                        </div>
                    </div>
                    <div className="research-leadership__content">
                        <span className="research-kicker">{leadership.kicker}</span>
                        <h2>{leadership.heading}</h2>
                        {(leadership.paragraphs || []).map((p, idx) => (
                            <p key={idx}>{p}</p>
                        ))}
                        <div className="research-leadership__links">
                            {(leadership.links || []).map((link) => (
                                <a key={link.label} href={mediaUrl(link.href)} target="_blank" rel="noreferrer">
                                    {LINK_ICONS[link.icon] || <FaExternalLinkAlt />}
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="research-focus">
                    <div className="research-focus__head">
                        <span className="research-kicker">{focus.kicker}</span>
                        <h2>{focus.heading}</h2>
                    </div>
                    <div className="research-focus__grid">
                        {(focus.cards || []).map((item) => (
                            <article key={item.title} className="research-focus-card">
                                <span className="icon">{FOCUS_ICONS[item.icon] || <FaMicroscope />}</span>
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="research-resources">
                    <div className="research-resources__head">
                        <span className="research-kicker">{resources.kicker}</span>
                        <h2>{resources.heading}</h2>
                    </div>
                    <div className="research-resources__list">
                        {(resources.links || []).map((link) => (
                            <a key={link.title} href={mediaUrl(link.href)} target="_blank" rel="noreferrer" className="research-resource-item">
                                <span>{link.title}</span>
                                <IoArrowForwardOutline />
                            </a>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ResearchMain;
